import { useParams, useNavigate } from "react-router-dom";
import { Header } from "@layouts/Header";
import { Footer } from "@layouts/Footer";
import { Reveal } from "@components/ui";
import { getJobBySlug, getApplyMailtoLink } from "../../data/jobs";
import copyIcon from "@assets/images/vuesax-bulk-copy.svg";

export function JobDetails() {
  const { jobSlug } = useParams<{ jobSlug: string }>();
  const navigate = useNavigate();
  const job = jobSlug ? getJobBySlug(jobSlug) : undefined;

  if (!job) {
    return (
      <div className="w-full flex flex-col bg-white">
        <section className="w-full bg-[#f24d2b] relative overflow-hidden">
          <div className="w-full max-w-[1728px] mx-auto flex flex-col items-start pt-8 px-5 md:px-10 lg:px-[121px] pb-12">
            <div className="w-full relative z-10">
              <Header />
            </div>
          </div>
        </section>
        <div className="flex flex-col items-center justify-center py-32 px-5">
          <h1 className="text-[32px] md:text-[48px] font-semibold text-[#122d63] tracking-[-0.04em]">
            Job Not Found
          </h1>
          <p className="text-[18px] md:text-[22px] text-[#62636c] mt-4 tracking-[-0.04em]">
            The position you're looking for doesn't exist or has been filled.
          </p>
          <button
            onClick={() => navigate("/careers")}
            className="mt-8 inline-flex items-center gap-2 bg-[#090d2c] text-white rounded-[40px] px-6 py-3 text-[16px] md:text-[18px] font-medium hover:bg-[#1a1f42] transition-all duration-300"
          >
            Back to Careers
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const mailtoLink = getApplyMailtoLink(job);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
  };

  const shareText = `We're hiring! ${job.title} – ${job.location}, ${job.type} at GAZ. Apply now!`;

  const shareOnLinkedIn = () => {
    // LinkedIn only supports URL — copies share text to clipboard first so user can paste
    navigator.clipboard.writeText(shareText);
    window.open(
      `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(shareText + "\n" + window.location.href)}`,
      "_blank"
    );
  };


  const shareOnX = () => {
    window.open(
      `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(window.location.href)}`,
      "_blank"
    );
  };

  return (
    <div className="w-full flex flex-col bg-white">
      {/* Header bar */}
      <section className="w-full bg-[#f24d2b] relative overflow-hidden">
        <div className="w-full max-w-[1728px] mx-auto flex flex-col items-start pt-8 px-5 md:px-10 lg:px-[121px] pb-12">
          <div className="w-full relative z-10">
            <Header />
          </div>
        </div>
      </section>

      {/* Main content */}
      <main className="w-full max-w-[1368px] mx-auto px-5 md:px-10 lg:px-0 pt-12 md:pt-20 pb-10 md:pb-[60px]">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
          {/* Sidebar */}
          <aside className="w-full lg:w-[234px] shrink-0 flex flex-col gap-10 lg:gap-12 pt-0 lg:pt-4">
            <Reveal>
              <button
                onClick={() => navigate("/careers")}
                className="flex items-center gap-2 group"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#f03813"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="rotate-180 group-hover:-translate-x-1 transition-transform duration-200"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
                <span className="text-[16px] leading-6 font-medium text-[#f03813]">
                  Back to All Jobs
                </span>
              </button>
            </Reveal>

            <Reveal>
              <div className="flex flex-col gap-4">
                <h3 className="text-[18px] lg:text-[22px] leading-7 text-[#62636c]">Share</h3>
                <div className="flex flex-wrap items-center gap-3">
                  {/* LinkedIn */}
                  <button
                    onClick={shareOnLinkedIn}
                    className="w-11 h-11 lg:w-14 lg:h-14 rounded-full bg-[#f1f1f1] flex items-center justify-center hover:bg-[#0077b5] hover:text-white text-[#62636c] transition-all duration-300"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="lg:w-6 lg:h-6">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </button>
                  {/* Instagram — copies link since IG has no web share */}
                  <button
                    onClick={handleCopyLink}
                    className="w-11 h-11 lg:w-14 lg:h-14 rounded-full bg-[#f1f1f1] flex items-center justify-center hover:bg-gradient-to-tr hover:from-[#f09433] hover:via-[#dc2743] hover:to-[#bc1888] hover:text-white text-[#62636c] transition-all duration-300"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="lg:w-6 lg:h-6">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                    </svg>
                  </button>
                  {/* X / Twitter */}
                  <button
                    onClick={shareOnX}
                    className="w-11 h-11 lg:w-14 lg:h-14 rounded-full bg-[#f1f1f1] flex items-center justify-center hover:bg-black hover:text-white text-[#62636c] transition-all duration-300"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="lg:w-5 lg:h-5">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </button>
                  {/* Copy link button */}
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-2 bg-[#ffe6e0] rounded-xl px-3 py-2 lg:px-4 lg:py-3 hover:bg-[#ffd4cb] transition-colors duration-200"
                  >
                    <span className="text-[14px] lg:text-[16px] leading-6 font-medium text-[#f03813]">
                      Job link
                    </span>
                    <img src={copyIcon} alt="Copy" className="w-5 h-5 lg:w-6 lg:h-6" />
                  </button>
                </div>
              </div>
            </Reveal>
          </aside>

          {/* Job content */}
          <div className="flex-1 flex flex-col gap-10 md:gap-12 min-w-0">
            <Reveal>
              <div className="flex flex-col gap-5">
                <span className="text-[16px] tracking-[0.15px] leading-6 font-medium text-[#f03813]">
                  Job Details
                </span>
                <h1 className="text-[32px] md:text-[40px] tracking-[-0.04em] font-medium text-[#111322]">
                  {job.title}
                </h1>
                <div className="flex flex-wrap gap-3 mt-1">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#f1f1f1] text-[14px] text-[#62636c]">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    {job.location}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#f1f1f1] text-[14px] text-[#62636c]">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                      <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
                    </svg>
                    {job.type}
                  </span>
                </div>
              </div>
            </Reveal>

            {/* About the Role */}
            <Reveal>
              <div className="flex flex-col gap-4">
                <h3 className="text-[24px] font-medium text-[#090d2c]">
                  About the Role
                </h3>
                <p className="text-[20px] md:text-[22px] tracking-[-0.4px] leading-[30px] text-[#62636c] text-justify">
                  {job.about}
                </p>
              </div>
            </Reveal>

            {/* Key Responsibilities */}
            <Reveal>
              <div className="flex flex-col gap-4">
                <h3 className="text-[24px] font-medium text-[#090d2c]">
                  Key Responsibilities
                </h3>
                <ul className="flex flex-col gap-3">
                  {job.responsibilities.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#f03813] shrink-0 mt-[11px]" />
                      <span className="text-[20px] md:text-[22px] tracking-[-0.4px] leading-[30px] text-[#62636c]">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Requirements */}
            <Reveal>
              <div className="flex flex-col gap-4">
                <h3 className="text-[24px] font-medium text-[#090d2c]">
                  Requirements
                </h3>
                <ul className="flex flex-col gap-3">
                  {job.requirements.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#122d63] shrink-0 mt-[11px]" />
                      <span className="text-[20px] md:text-[22px] tracking-[-0.4px] leading-[30px] text-[#62636c]">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* What We Offer */}
            <Reveal>
              <div className="flex flex-col gap-4">
                <h3 className="text-[24px] font-medium text-[#090d2c]">
                  What We are Offering for this Role
                </h3>
                <ul className="flex flex-col gap-3">
                  {job.offering.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a7a4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-[5px]">
                        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                      <span className="text-[20px] md:text-[22px] tracking-[-0.4px] leading-[30px] text-[#62636c]">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Apply button */}
            <Reveal>
              <a
                href={mailtoLink}
                className="inline-flex items-center gap-3 bg-[#090d2c] text-white rounded-[40px] py-3 pl-5 pr-3 text-[18px] md:text-[20px] tracking-[-0.04em] font-medium hover:bg-[#1a1f42] hover:shadow-[0_4px_16px_rgba(9,13,44,0.3)] transition-all duration-300 w-fit"
              >
                Apply for this Position
                <svg className="w-10 h-10 rounded-full bg-white/10 p-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </Reveal>
          </div>
        </div>
      </main>

      {/* Contact HR banner */}
      <section className="w-full px-5 md:px-10 lg:px-[180px] pb-12 md:pb-16 lg:pb-[120px]">
        <Reveal>
          <div className="w-full max-w-[1368px] mx-auto rounded-[24px] bg-[#f24d2b] overflow-hidden relative">
            {/* Decorative bars */}
            <div className="absolute top-[-112px] left-[60%] bg-[#fff8f6] w-[31px] h-[251px] opacity-10 rotate-[0deg]" />
            <div className="absolute top-[-150px] left-[65%] bg-[#fff8f6] w-[31px] h-[251px] opacity-10 rotate-[0deg]" />
            <div className="absolute bottom-[-80px] right-[15%] bg-[#fff8f6] w-[31px] h-[302px] opacity-10 rotate-[45deg]" />
            <div className="absolute bottom-[-60px] right-[8%] bg-[#fff8f6] w-[31px] h-[302px] opacity-10 rotate-[75deg]" />

            <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-16 p-8 md:p-12 lg:py-14 lg:px-[80px]">
              <div className="flex-1">
                <h2 className="text-[32px] md:text-[44px] lg:text-[60px] font-bold tracking-[-0.04em] text-white">
                  Contact HR
                </h2>
                <p className="text-[20px] md:text-[24px] tracking-[-0.04em] text-[#fff8f6] mt-2">
                  For recruitment inquiries:
                </p>
              </div>
              <div className="rounded-[24px] bg-[#090d2c] p-6 flex flex-col gap-3">
                <h3 className="text-[24px] md:text-[28px] font-bold tracking-[-0.04em] text-white leading-[38px]">
                  Contact HR
                </h3>
                <div className="flex flex-col gap-2">
                  <a href="mailto:careers@gazglobal.net" className="flex items-center gap-3 text-white hover:text-white/80 transition-colors">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                    </svg>
                    <span className="text-[18px] md:text-[20px] tracking-[-0.04em] font-medium">
                      careers@gazglobal.net
                    </span>
                  </a>
                  <div className="flex items-center gap-3 text-white/75">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                    </svg>
                    <span className="text-[16px] md:text-[18px] tracking-[-0.04em]">
                      800-GAZ (42747)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}

export default JobDetails;
